import { Modal } from 'antd';
import { ConfigProvider } from 'antd-v5';
import AuthenticatedRoute from 'components/AuthenticatedRoute';
import withSuspenseFallback from 'components/hoc/withSuspenseFallback';
import { WidgetEmpty } from 'components/widgets/WidgetEmpty';
import { getAntdV5Theme } from 'constants/AntdV5Theme';
import { ROLE_SUPERADMIN, ROLE_SUPERADMINREAD } from 'constants/RBACConstants';
import ErrorBoundary from 'containers/ErrorBoundary';
import { useKeyboardNavigation } from 'hooks/useKeyboardNavigation';
import TitleManager from 'less/components/TitleManager';
import React, { useEffect, useState } from 'react';
import {
  Route,
  RouteComponentProps,
  Switch,
  useHistory,
} from 'react-router-dom';
import { isInIframe } from 'utils/iframeUtils';
import ModalUtil from 'utils/modalUtil';
import PageNotFound from 'views/PageNotFound/PageNotFound';

const Login = withSuspenseFallback(
  React.lazy(() => import('views/Login/Login')),
);
const Logout = withSuspenseFallback(
  React.lazy(() => import('views/Logout/Logout')),
);
const Register = withSuspenseFallback(
  React.lazy(() => import('views/Register/Register')),
);
const TenantSwitcher = withSuspenseFallback(
  React.lazy(() => import('views/TenantSwitcher/TenantSwitcher')),
);
const CreateTenant = withSuspenseFallback(
  React.lazy(() => import('views/CreateTenant/CreateTenant')),
);
const ACIconGallery = withSuspenseFallback(
  React.lazy(() => import('views/ACIconGallery/ACIconGallery')),
);
const TotpEnroll = withSuspenseFallback(
  React.lazy(() => import('views/Login/TotpEnroll')),
);
const AzureExtensionConfig = withSuspenseFallback(
  React.lazy(() => import('views/AzureExtension/AzureExtensionConfig')),
);
const AzureExtension = withSuspenseFallback(
  React.lazy(() => import('views/AzureExtension/AzureExtension')),
);
const AzureExtensionDetail = withSuspenseFallback(
  React.lazy(() => import('views/AzureExtension/AzureExtensionDetail')),
);
const SuperAdminMain = withSuspenseFallback(
  React.lazy(() => import('containers/SuperAdminMain')),
);
const Main = withSuspenseFallback(
  React.lazy(() => import('containers/Main/Main')),
);
const SeekAccess = withSuspenseFallback(
  React.lazy(() => import('views/SeekAccess/SeekAccess')),
);
const OAuthConsent = withSuspenseFallback(
  React.lazy(() => import('views/OAuthConsent/OAuthConsent')),
);
const AWSMarketPlaceSignUp = withSuspenseFallback(
  React.lazy(() => import('views/AWSMarketPlaceSignUp/AWSMarketPlaceSignUp')),
);
const ClearLocalStorage = withSuspenseFallback(
  React.lazy(() => import('views/ClearLocalStorage')),
);

const AppRouteFrame = ({
  children,
  heading,
}: {
  children: React.ReactNode;
  heading?: string;
}) => (
  <main className="app-route-frame">
    {heading && <h1 className="sr-only">{heading}</h1>}
    {children}
  </main>
);

type T = RouteComponentProps;

const renderFramedRoute =
  (Component: React.ComponentType<T>, heading: string) => (props: T) => (
    <AppRouteFrame heading={heading}>
      <Component {...props} />
    </AppRouteFrame>
  );

const FramedRoute = ({
  Component,
  heading,
  path,
}: {
  Component: React.ComponentType<T>;
  heading: string;
  path: string;
}) => {
  return <Route path={path} render={renderFramedRoute(Component, heading)} />;
};

export default function App() {
  const [isHighContrastMode, setIsHighContrastMode] = useState(false);
  const history = useHistory();

  // Global keyboard navigation detection for focus indicator styling
  useKeyboardNavigation();

  useEffect(() => {
    const unlisten = history.listen(() => {
      Modal.destroyAll();
      ModalUtil.closeAll();
    });
    return () => unlisten();
  }, [history]);

  const isIframe = isInIframe();
  const isLocalhost = window.location.hostname === 'localhost';

  return (
    <ConfigProvider
      theme={getAntdV5Theme(isHighContrastMode, setIsHighContrastMode)}
      prefixCls="ant-v5"
      renderEmpty={() => <WidgetEmpty />}
    >
      <ErrorBoundary>
        <TitleManager />
        <Switch>
          {!isIframe && (
            <FramedRoute
              path="/logout"
              Component={Logout}
              heading="Sign out of ArmorCode"
            />
          )}
          {!isIframe && (
            <FramedRoute
              path="/login/totp-enroll"
              Component={TotpEnroll}
              heading="Enroll multi-factor authentication"
            />
          )}
          {!isIframe && (
            <FramedRoute
              path="/login"
              Component={Login}
              heading="Sign in to ArmorCode"
            />
          )}
          {!isIframe && (
            <FramedRoute
              path="/register"
              Component={Register}
              heading="Create ArmorCode account"
            />
          )}
          <FramedRoute
            path="/404"
            Component={PageNotFound}
            heading="Page not found"
          />
          {isLocalhost && (
            <Route path="/iconGallery" component={ACIconGallery} />
          )}
          {!isIframe && (
            <AuthenticatedRoute
              key="oauthConsent"
              path="/oauth/consent"
              component={OAuthConsent}
            />
          )}
          {!isIframe && (
            <FramedRoute
              path="/seekAccess"
              Component={SeekAccess}
              heading="Request access"
            />
          )}
          {!isIframe && (
            <FramedRoute
              path="/mktplaceSignUp"
              Component={AWSMarketPlaceSignUp}
              heading="Complete AWS Marketplace signup"
            />
          )}
          {!isIframe && (
            <FramedRoute
              path="/clear-local"
              Component={ClearLocalStorage}
              heading="Clear local data"
            />
          )}
          {isIframe && (
            <Route
              path="/azure-extension/findings"
              component={AzureExtension}
            />
          )}
          {isIframe && (
            <Route
              path="/azure-extension/config"
              component={AzureExtensionConfig}
            />
          )}
          {isIframe && (
            <Route
              path="/azure-extension/findingDetail/:findingId"
              component={AzureExtensionDetail}
            />
          )}

          {/* if iframe then route all paths to azure, to avoid accidental display of internal pages */}
          {isIframe && <Route path="/" component={AzureExtensionConfig} />}
          {!isIframe && (
            <AuthenticatedRoute
              key="superAdmin"
              path="/superAdmin"
              component={SuperAdminMain}
              accessRoles={[ROLE_SUPERADMIN, ROLE_SUPERADMINREAD]}
            />
          )}
          {!isIframe && (
            <AuthenticatedRoute
              key="tenantSwitch"
              path="/tenantSwitch"
              component={TenantSwitcher}
            />
          )}
          {!isIframe && (
            <AuthenticatedRoute
              key="createTenant"
              path="/createTenant"
              component={CreateTenant}
            />
          )}
          {!isIframe && (
            <AuthenticatedRoute key="app" path="/" component={Main} />
          )}
        </Switch>
      </ErrorBoundary>
    </ConfigProvider>
  );
}
